// web/src/tutorial/quickstart/harp-glide.ts
// Harp Glide · 快速上手（最短流程版）
//
// 定位：基本不调参数 —— 选演奏者 → 选 MIDI → 生成动画。
// 参数细节一律下沉到 /details/harp-glide.html，本页不重复参数表。
//
// 依据：G:\harp_glide_rust
//   src/ui/app.rs（四个页签与顺序）、avatar_panel.rs（参数设置页）、midi_panel.rs（MIDI 信息页）、
//   execute_panel.rs（生成动画页）、avatar_manager.rs（演奏者列表来源）、
//   src/harp_glider.rs 与 src/animate/animator.rs（输出文件名与目录）、
//   assets/i18n/zh-CN.json 与 en-US.json（界面原文）

import { l } from "../l";
import type { TutorialDoc } from "../types";

export const harpGlideQuickstart: TutorialDoc = {
  key: "harp_glide",
  slug: "harp-glide",
  kind: "quickstart",
  icon: "🎵",
  title: l("Harp Glide 竖琴演奏动画", "Harp Glide Harp Performance"),
  subtitle: l(
    "选一位竖琴演奏者、选一份 MIDI，点一下生成，就能得到可以加载进 Blender / Unreal 的竖琴演奏动画。这一篇只讲最短跑通路径，参数全部保持默认。",
    "Pick a harpist, pick a MIDI file, press generate — and you get harp performance animation ready to load in Blender / Unreal. This page covers only the shortest working path, with every parameter left at its default.",
  ),
  readMinutes: 6,
  video: {
    url: "https://www.bilibili.com/video/BV13hJH6cEAX/",
    youtube: "https://www.youtube.com/watch?v=lG-akWZm3fc",
    caption: l(
      "Harp Glide 完整操作演示：HarpGlide主流程跑通！芭芭拉冲呀！（B 站）",
      "Harp Glide full walkthrough: First HarpGlide Video - Barbara Play Vourukasha Oasis BGM (YouTube)",
    ),
  },
  images: [
    {
      src: "/assets/image/products/harp-glide/CN/参数设置.png",
      srcEn: "/assets/image/products/harp-glide/EN/Settings.png",
      label: l("参数设置", "Settings"),
      // 中英两版窗口大小不同（CN 862×892 / EN 1043×1034），声明取上限
      width: 1043,
      height: 1034,
    },
    {
      src: "/assets/image/products/harp-glide/CN/MIDI信息.png",
      srcEn: "/assets/image/products/harp-glide/EN/MIDI Info'.png",
      label: l("MIDI 信息", "MIDI Info"),
      width: 862,
      height: 892,
    },
    {
      src: "/assets/image/products/harp-glide/CN/生成动画.png",
      srcEn: "/assets/image/products/harp-glide/EN/Animation.png",
      label: l("生成动画", "Animation"),
      width: 862,
      height: 892,
    },
    {
      src: "/assets/image/products/harp-glide/CN/Pose查看器.png",
      srcEn: "/assets/image/products/harp-glide/EN/Poses Viewer.png",
      label: l("Poses 查看", "Poses Viewer"),
      width: 862,
      height: 892,
    },
  ],
  sections: [
    // ─────────────────────────────────────────────────────────────
    {
      id: "overview",
      title: l("这一篇要做什么", "What you will do"),
      blocks: [
        {
          kind: "p",
          text: l(
            "Harp Glide 是竖琴演奏动画生成器。给它**一份 MIDI** 和**一位演奏者**，它会自己算出手掌、手指、双脚踏板、头部、竖琴本体和琴弦振动的全部关键帧，写出几个数据文件；你在 Blender 或 Unreal 里用 MusicDoll 插件加载这些文件，就能直接播放或渲染——整个过程你不需要摆任何一帧。",
            "Harp Glide generates harp performance animation. Give it **a MIDI file** and **a performer**, and it computes every keyframe for palms, fingers, both feet on the pedals, the head, the harp body and string vibration, then writes out a handful of data files. Load those with the MusicDoll plugin in Blender or Unreal and the performance is ready to play or render — you never key a single frame by hand.",
          ),
        },
        {
          kind: "steps",
          items: [
            {
              title: l("在 `参数设置` 里选演奏者", "Pick a performer under `参数设置` / Settings"),
              text: l(
                "演奏者是软件自带的，下拉菜单里选一个就行。本页不调任何参数。",
                "Performers ship with the app — just choose one from the dropdown. No parameter changes on this page.",
              ),
            },
            {
              title: l("在 `MIDI 信息` 里选 MIDI 并勾轨道", "Load a MIDI and tick tracks under `MIDI 信息` / MIDI Info"),
              text: l(
                "选文件（或直接把文件拖进窗口）、扫描轨道、勾上要生成动画的那一条。`音符量化` 保持默认 `无量化`。",
                "Choose the file (or drag it into the window), scan the tracks, and tick the one you want animated. Leave `音符量化` / Note Quantization at its default `无量化` / None.",
              ),
            },
            {
              title: l("在 `生成动画` 里点一下按钮", "Press the button under `生成动画` / Generate Animation"),
              text: l(
                "核对左边的配置概览，点 `⚡ 生成动画`，等控制台跑完，拿到 `.harpglide` 报告文件。",
                "Check the overview on the left, press `⚡ 生成动画` / Generate Animation, let the console finish, and collect the `.harpglide` report.",
              ),
            },
          ],
        },
        {
          kind: "callout",
          tone: "tip",
          text: l(
            "第一次跑，建议先用自带演奏者 + 一首单轨的竖琴或钢琴独奏 MIDI，几分钟就能跑完一轮，确认输出目录和插件加载都正常之后再换自己的曲子。",
            "For a first run, use a bundled performer plus a single-track solo harp or piano MIDI — it finishes in minutes and confirms your output folder and plugin loading before you move on to your own material.",
          ),
        },
      ],
      image: { image: 0 },
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "tuning",
      title: l("只想跑通？一个参数都不用改", "Running it through? Change nothing"),
      blocks: [
        {
          kind: "p",
          text: l(
            "本页介绍的流程里，除了选演奏者、选 MIDI、勾轨道这三个动作，**其余全部保持默认值**即可出结果。默认值已经是作者调好的一套通用配置：快慢适中、动作不算夸张，适合大多数独奏曲。",
            "Apart from picking a performer, picking a MIDI and ticking a track, **everything else stays at its default** and still produces a result. Those defaults are a general-purpose set the author tuned: moderate tempo, restrained motion, fine for most solo pieces.",
          ),
        },
        {
          kind: "p",
          text: l(
            "需要细调的时候（手指动作太急、脚跟不上转调、手型看着别扭）再看[软件界面详细说明](/details/harp-glide.html)——那里按页签逐个讲了每一个可调参数的取值范围、默认值和调节方向。",
            "When you do need to tune something — the fingers feel rushed, the feet lag behind a key change, a hand shape looks wrong — read the [software UI reference](/details/harp-glide.html). It walks every tab and every adjustable parameter with its range, default and which way to move it.",
          ),
        },
      ],
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "prepare",
      title: l("开始之前：只需要一份 MIDI", "Before you start: just a MIDI file"),
      blocks: [
        {
          kind: "p",
          text: l(
            "要准备的只有一份 MIDI 文件（`.mid` 或 `.midi`）。演奏者不是你要准备的东西——软件自带示例演奏者，启动后在 `参数设置` 页的下拉菜单里就能直接选。要把自己的人物做成可用演奏者，是另一条路，见[自定义演奏者进阶教程](/advanced/harp-glide.html)。",
            "The only thing you need is a MIDI file (`.mid` or `.midi`). Performers are not something you prepare — the app ships with examples, selectable from the dropdown on the `参数设置` / Settings tab. Turning your own character into a performer is a separate path; see the [custom performer tutorial](/advanced/harp-glide.html).",
          ),
        },
        {
          kind: "ul",
          items: [
            l(
              "**MIDI 文件**：独奏曲最好上手。同时发声的音符太多的编曲，手型会排得很勉强，甚至算不出可行指法。",
              "**A MIDI file**: solo pieces are easiest. Arrangements with many notes sounding at once force awkward hand shapes and can even leave no playable fingering.",
            ),
            l(
              "**不用准备演奏者**：自带示例演奏者已经包含竖琴的弦数、手位、抱琴姿势等全部基准数据。",
              "**No performer needed**: the bundled examples already carry the harp's string count, hand positions and holding poses.",
            ),
            l(
              "**不用准备输出目录**：程序固定写到自己目录下的 `output/`，界面上没有改路径的入口。",
              "**No output folder needed**: files always go to `output/` next to the executable; there is no path setting in the UI.",
            ),
          ],
        },
      ],
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "ui-overview",
      title: l("界面总览：四个页签，前三个是主流程", "The interface: four tabs, the first three matter"),
      blocks: [
        {
          kind: "p",
          text: l(
            "窗口顶栏从左到右是四个页签。前三个按顺序点一遍就跑完了整条流程，第四个是可选的调试工具。",
            "Four tabs run across the top of the window. Click the first three in order and the whole pipeline is done; the fourth is an optional debugging tool.",
          ),
        },
        {
          kind: "table",
          head: [l("页签", "Tab"), l("干什么", "What it is for"), l("最短流程要不要点", "Needed?")],
          rows: [
            [
              l("`参数设置`", "`参数设置` / Settings"),
              l(
                "选演奏者、管理演奏者列表，以及全部动作和技法参数。",
                "Choose and manage performers, plus every motion and technique setting.",
              ),
              l("要，第 1 步", "Yes — step 1"),
            ],
            [
              l("`MIDI 信息`", "`MIDI 信息` / MIDI Info"),
              l(
                "选 MIDI 文件、扫描轨道、勾选要生成动画的轨道。",
                "Load the MIDI, scan its tracks and tick the tracks to animate.",
              ),
              l("要，第 2 步", "Yes — step 2"),
            ],
            [
              l("`生成动画`", "`生成动画` / Generate Animation"),
              l(
                "回显当前配置，一键跑完全部阶段，右侧控制台输出全过程日志。",
                "Echoes the current setup, runs every stage in one click, and streams the log to the console.",
              ),
              l("要，第 3 步", "Yes — step 3"),
            ],
            [
              l("`Poses 查看`", "`Poses 查看` / Poses Viewer"),
              l(
                "只读的调试页：按帧号浏览算出来的每个手型，排查指法问题用。",
                "A read-only debugging tab: browse the computed poses by frame to investigate fingering.",
              ),
              l("不用，可跳过", "No — optional"),
            ],
          ],
        },
        {
          kind: "callout",
          tone: "info",
          text: l(
            "顶栏右边的 `语言` 菜单、`帮助` 菜单和 `暗色主题` 勾选框只影响界面本身，跑流程时可以完全不管。截图里是深色主题，浅色主题下控件位置完全一样。",
            "The `语言` / Language menu, `帮助` / Help menu and `暗色主题` / Dark theme checkbox on the right only affect the interface itself — ignore them while you work. The screenshots use the dark theme; control positions are identical in the light theme.",
          ),
        },
      ],
      image: { image: 1 },
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "performer",
      title: l("第 1 步：选一位演奏者", "Step 1 — pick a performer"),
      blocks: [
        {
          kind: "p",
          text: l(
            "打开 `参数设置`。左上角 `基础设置` 卡片里那个下拉菜单就是演奏者列表，点开、选一个，这一步就完成了。选中的演奏者会立刻成为本次生成要用的角色。",
            "Open `参数设置` / Settings. The dropdown in the top-left `基础设置` / Basic Settings card is the performer list — open it, pick one, done. The choice immediately becomes the performer this run will use.",
          ),
        },
        {
          kind: "ol",
          items: [
            l(
              "点开左上角的下拉菜单，里面列着软件当前认得的全部演奏者。",
              "Open the dropdown at the top left; it lists every performer the app currently knows about.",
            ),
            l(
              "选一个名字。右边 `演奏者信息` 卡片会立刻显示这个演奏者的图片，说明已经切换过去了。",
              "Pick a name. The `演奏者信息` / Avatar Info card on the right immediately shows that performer's picture, confirming the switch.",
            ),
            l(
              "下拉菜单正下方的 `记录池大小:` 与 `目标帧率:` 保持原样（默认 `400` / `60.0 fps`），本页其它滑块和勾选框也都不用动。",
              "Leave `记录池大小:` / Pool size: and `目标帧率:` / Target FPS: just below the dropdown as they are (defaults `400` / `60.0 fps`), and leave the other sliders and checkboxes on this page alone.",
            ),
          ],
        },
        {
          kind: "callout",
          tone: "tip",
          text: l(
            "演奏者列表是空的、或者你想加一个自己的角色，就用 `演奏者信息` 卡片下面的 `➕ 新增` 按钮；改名字或换配置文件用 `✏ 修改`。这两个按钮怎么填、填错了会提示什么，都写在[软件界面详细说明](/details/harp-glide.html)里。",
            "If the list is empty, or you want to add your own character, use the `➕ 新增` / Add button under the `演奏者信息` / Avatar Info card; `✏ 修改` / Edit edits the current one. How to fill those forms and what the errors mean is covered in the [software UI reference](/details/harp-glide.html).",
          ),
        },
      ],
      image: { image: 0 },
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "midi",
      title: l("第 2 步：选 MIDI、扫描、勾轨道", "Step 2 — load the MIDI, scan, tick a track"),
      blocks: [
        {
          kind: "p",
          text: l(
            "切到 `MIDI 信息`。左栏是「用哪份文件」，右栏是「生成哪条轨」，从左到右按下面三步做完。",
            "Switch to `MIDI 信息` / MIDI Info. The left column decides which file, the right column decides which track — work left to right through the three steps below.",
          ),
        },
        {
          kind: "ol",
          items: [
            l(
              "点 `📂 选择 MIDI 文件` 选一份 `.mid` / `.midi`；也可以直接把文件拖进窗口，左栏下方那块虚线区 `📥 将 MIDI 文件拖放到此处` 就是拖放位置。",
              "Click `📂 选择 MIDI 文件` / Select MIDI File and pick a `.mid` / `.midi` — or simply drag the file into the window; the dashed `📥 将 MIDI 文件拖放到此处` / Drop a MIDI file here area at the bottom of the left column is the drop target.",
            ),
            l(
              "点 `🔍 扫描轨道信息`。程序解析这份 MIDI，把含音符的轨道列到右栏，并在控制台写下「MIDI 扫描完成，共 {} 条有效轨道」。",
              "Click `🔍 扫描轨道信息` / Scan Track Info. The app parses the file, lists the tracks that contain notes in the right column, and logs “MIDI 扫描完成，共 {} 条有效轨道”.",
            ),
            l(
              "在右栏 `轨道选择` 里，把**要生成动画的那一条**轨道勾上（最左边的勾选框）。竖琴曲通常只勾竖琴那条；伴奏、打击轨不要勾。",
              "In the `轨道选择` / Track Selection card on the right, tick **the track you want animated** (leftmost checkbox). A harp piece usually needs only the harp track — leave accompaniment and percussion unticked.",
            ),
          ],
        },
        {
          kind: "ul",
          items: [
            l(
              "勾错了想重来：用 `全选` / `全不选` 批量处理，再手工调整个别轨。",
              "Ticked the wrong thing? Use `全选` / Select All / `全不选` / Deselect All for bulk work, then adjust individual tracks by hand.",
            ),
            l(
              "勾选**即时生效**，没有「添加」按钮，也就不用担心忘记确认——执行阶段直接读这个勾选状态。",
              "Ticking takes effect **immediately** — there is no “add” button and nothing to confirm; generation reads this state directly.",
            ),
            l(
              "`🎵 泛音` 勾选框这次先不用管，只有整条轨都是泛音声部时才需要勾。",
              "Ignore the `🎵 泛音` / Harmonic checkbox for now — it is only needed when an entire track is a harmonic part.",
            ),
            l(
              "左栏 `参数设置` 卡片里的 `音符量化` 保持默认 `无量化`。只有真人弹奏、时间点很毛躁的素材才考虑换成 `1/16` 或 `1/32`。",
              "Leave `音符量化` / Note Quantization in the left column's `参数设置` / Settings card at its default `无量化` / None. Quantization is only worth considering for human-played material with loose timing, where `1/16` or `1/32` may help.",
            ),
          ],
        },
        {
          kind: "callout",
          tone: "warn",
          text: l(
            "重新选文件或拖进新文件会**清空上一次的扫描结果**，得再点一次 `🔍 扫描轨道信息`。另外左栏那张 `MIDI 信息` 卡片不是每次都出现——这份文件的速度完全规整时它会隐藏，属于正常现象。",
            "Selecting or dropping a new file **clears the previous scan**, so press `🔍 扫描轨道信息` / Scan Track Info again. The `MIDI 信息` / MIDI Info card in the left column does not always appear either — it stays hidden when the file's tempo is completely regular, which is normal.",
          ),
        },
      ],
      image: { image: 1 },
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "execute",
      title: l("第 3 步：点一下，生成动画", "Step 3 — press generate"),
      blocks: [
        {
          kind: "p",
          text: l(
            "切到 `生成动画`。左栏 `配置概览` 把这次要用的东西回显一遍，确认 `当前演奏者`、`当前 MIDI`、`选中轨道` 都对得上，然后点 `⚡ 生成动画`。",
            "Switch to `生成动画` / Generate Animation. The `配置概览` / Configuration Overview card on the left echoes what this run will use — confirm `当前演奏者` / Current Avatar, `当前 MIDI` / Current MIDI and `选中轨道` / Selected tracks look right, then press `⚡ 生成动画` / Generate Animation.",
          ),
        },
        {
          kind: "p",
          text: l(
            "按钮在左栏 `执行操作` 卡片里，它跑的是全部阶段：MIDI 预处理 → 踏板规划 → 音符处理 → 弦映射 → 指法与踏板序列搜索 → 动画生成与写文件。运行期间按钮下面会出现转圈和「正在执行...」，跑完之后**没有弹窗**——结果去右栏 `控制台输出` 里看。",
            "The button lives in the `执行操作` / Actions card on the left and runs every stage: MIDI preprocessing → pedal planning → note processing → string mapping → fingering and pedal sequence search → animation generation and file writing. A spinner and “正在执行... / Running...” appear while it works, and **no dialog pops up** when it finishes — read the result in the `控制台输出` / Console Output panel.",
          ),
        },
        {
          kind: "callout",
          tone: "tip",
          title: l("控制台里看到什么算成功", "What success looks like in the console"),
          text: l(
            "日志一路往下滚，最后落到绿色那行 `完成: 流水线成功，生成 5 个文件`，接着是各输出文件的路径，最后一行是 `=== 流水线结束 ===`。看到绿色这行，就说明跑通了。",
            "The log scrolls on and settles on the green line `完成: 流水线成功，生成 5 个文件`, followed by the paths of every output file and finishing with `=== 流水线结束 ===`. That green line means you are done.",
          ),
        },
        {
          kind: "ul",
          items: [
            l(
              "想留下这次运行的地址：点控制台上的 `📋 复制结果`，它把报告文件的完整路径复制到剪贴板。",
              "To keep this run's location: press `📋 复制结果` / Copy Result on the console — it copies the report file's full path to the clipboard.",
            ),
            l(
              "运行中**没有取消按钮**，想中断只能关掉程序。已经写出的中间文件会留在 `output/` 里，下次运行覆盖同名文件。",
              "There is **no cancel button** while running; the only way to stop is to close the app. Intermediate files already written stay in `output/` and are overwritten by the next run.",
            ),
          ],
        },
      ],
      image: { image: 2 },
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "result",
      title: l("生成出来的是什么、放在哪", "What you get and where it lands"),
      blocks: [
        {
          kind: "p",
          text: l(
            "一轮生成会在程序目录下的 `output/` 里写出五个文件，它们共用同一个前缀，格式是 `{MIDI文件名}_tracks_{轨道号}_{演奏者名}`。要让插件加载的是其中那一个 **`.harpglide`** 报告文件。",
            "A run writes five files under `output/` next to the executable, all sharing one prefix: `{midi}_tracks_{track index}_{performer}`. The one to hand to the plugin is the **`.harpglide`** report.",
          ),
        },
        {
          kind: "table",
          head: [l("文件", "File"), l("里面是什么", "What is inside")],
          rows: [
            [
              l(
                "`output/final_results/{前缀}.harpglide`",
                "`output/final_results/{prefix}.harpglide`",
              ),
              l(
                "报告文件：记录另外四个数据文件的位置。**插件加载的就是它。**",
                "The report: it records where the other four data files live. **This is what the plugin loads.**",
              ),
            ],
            [
              l(
                "`output/performance/{前缀}.performance_anim`",
                "`output/performance/{prefix}.performance_anim`",
              ),
              l(
                "演奏者动画：双手、双脚、头部与 Mid_Hand 的关键帧。",
                "Performer animation: keyframes for both hands, both feet, the head and the Mid_Hand midpoint.",
              ),
            ],
            [
              l(
                "`output/string/{前缀}.string_anim`、`output/harp/{前缀}.harp_anim`、`output/pedal_shape/{前缀}.pedal_shape`",
                "`output/string/{prefix}.string_anim`, `output/harp/{prefix}.harp_anim`, `output/pedal_shape/{prefix}.pedal_shape`",
              ),
              l(
                "弦振动事件、竖琴本体的位置与朝向、踏板档位变化——分别驱动琴弦、乐器本体和踏板的形变。",
                "String vibration events, the harp body's transform, and pedal position changes — driving the strings, the instrument body and the pedals respectively.",
              ),
            ],
          ],
        },
        {
          kind: "p",
          text: l(
            "接下来在 Blender 或 Unreal 里：启用 MusicDoll 插件 → 选中**和生成时同一位**演奏者 → 加载这份 `.harpglide`。插件会读取上面那四份数据，把动作写进时间线，之后正常播放或渲染即可。",
            "Then in Blender or Unreal: enable the MusicDoll plugin → select **the same performer used for generation** → load the `.harpglide`. The plugin reads the four data files, writes the motion onto the timeline, and you can play or render as usual.",
          ),
        },
        {
          kind: "callout",
          tone: "warn",
          text: l(
            "`.harpglide` 里记的是另外四个文件的**绝对路径**，所以别只把它一个拷到别的机器上——那四个文件要一起带走并保持位置不变，或者干脆在新机器上重新生成一遍。",
            "The `.harpglide` stores **absolute paths** to the other four files, so do not copy it to another machine on its own — take those four along and keep their layout, or simply regenerate there.",
          ),
        },
      ],
      image: { image: 2 },
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "checklist",
      title: l("跑通前自检清单", "Checklist before you run"),
      blocks: [
        {
          kind: "table",
          head: [l("检查项", "Check"), l("在哪看", "Where"), l("不对怎么办", "If it is wrong")],
          rows: [
            [
              l("选好了演奏者", "A performer is selected"),
              l("`参数设置` 左上角的下拉菜单显示着一个名字", "The dropdown at the top left of `参数设置` / Settings shows a name"),
              l(
                "下拉菜单为空就用 `➕ 新增` 添加一个。",
                "If the dropdown is empty, add one with `➕ 新增` / Add.",
              ),
            ],
            [
              l("MIDI 已选中并扫描过", "The MIDI is loaded and scanned"),
              l("左栏 `当前文件:` 有文件名，右栏 `轨道选择` 里有条目", "`当前文件:` / Current file: shows a name and `轨道选择` / Track Selection has entries"),
              l(
                "没扫描就点 `🔍 扫描轨道信息`；仍为空说明这份 MIDI 没有含音符的轨道，换一份。",
                "Press `🔍 扫描轨道信息` / Scan Track Info; if it stays empty the MIDI has no tracks with notes — try another file.",
              ),
            ],
            [
              l("至少勾了一条轨道", "At least one track is ticked"),
              l("`轨道选择` 副标题写着「已选 1 / 2 条轨道」之类的字样", "The `轨道选择` / Track Selection subtitle reads something like “已选 1 / 2 条轨道 / 1 / 2 tracks selected / 1 / 2 tracks selected”"),
              l(
                "在目标轨道最左边打勾，或先点 `全选` 再取消不要的。",
                "Tick the leftmost box of the target track, or use `全选` / Select All first and untick the rest.",
              ),
            ],
            [
              l("配置概览里没有「未选择」", "No “未选择” left in the overview"),
              l("`生成动画` 左栏 `配置概览` 的每一项都有具体值", "Every row in `配置概览` / Configuration Overview shows a real value"),
              l(
                "按提示回对应的页签补上——概览里写着该去哪一页。",
                "Follow the hint back to the tab it names and fill the gap.",
              ),
            ],
          ],
        },
      ],
      image: { image: 3 },
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "faq",
      title: l("常见问题", "FAQ"),
      blocks: [
        {
          kind: "p",
          text: l(
            "**点了生成但控制台只多了一条红色错误？** 说明前置条件少了一项。程序按固定顺序检查 MIDI 文件 → 演奏者文件 → 音轨，只报第一个缺的：`错误: 未选择 MIDI 文件` / `错误: 未选择演奏者文件` / `错误: 未选择任何音轨`。补上再点一次。",
            "**I pressed generate and the console only gained one red error.** A prerequisite is missing. The app checks in a fixed order — MIDI file → performer file → tracks — and reports only the first gap: `错误: 未选择 MIDI 文件` / Select MIDI File, `错误: 未选择演奏者文件` or `错误: 未选择任何音轨`. Fill it and press again.",
          ),
        },
        {
          kind: "p",
          text: l(
            "**拖进去的 MIDI 没反应？** 拖放时会检查扩展名，不是 `.mid` / `.midi` 就报「错误：文件扩展名不正确」，没有扩展名则报「错误：拖放的文件没有扩展名」。这两种情况下之前选中的文件仍然有效，换一个正确的文件重拖即可。",
            "**I dragged a MIDI in and nothing happened.** The drop checks the extension: anything other than `.mid` / `.midi` raises “错误：文件扩展名不正确”, and a file with no extension raises “错误：拖放的文件没有扩展名”. In both cases your previous selection stays valid — just drop a proper file.",
          ),
        },
        {
          kind: "p",
          text: l(
            "**跑起来很慢正常吗？** 正常，慢主要慢在指法搜索那一阶段，曲子越长越明显。控制台会逐阶段报进度，能看到它没卡住。想快一点的办法在[软件界面详细说明](/details/harp-glide.html)的 `记录池大小:` 一节。",
            "**Is it supposed to take this long?** Yes — the slow part is the fingering search, and it grows with the length of the piece. The console reports progress stage by stage so you can see it is still moving. Ways to speed it up are in the `记录池大小:` / Pool size: section of the [software UI reference](/details/harp-glide.html).",
          ),
        },
        {
          kind: "p",
          text: l(
            "**手型或踏板动作看起来不对，从哪查？** 到 `Poses 查看`，把可疑的帧号填进 `帧数:`，用 `±` 调搜索范围，点 `🔍 搜索`，然后看那几帧里每根手指被分到了哪根弦、拨弦方向是内还是外。确认是参数问题后，参数怎么调看[软件界面详细说明](/details/harp-glide.html)。",
            "**The hand shapes or pedal motion look wrong — where do I look?** Go to `Poses 查看` / Poses Viewer, type the suspicious frame into `帧数:` / Frame:, widen the search with `±`, press `🔍 搜索` / Search, and inspect which string each finger got and whether its pluck direction is inward or outward. If it turns out to be a parameter problem, the [software UI reference](/details/harp-glide.html) covers how to tune each one.",
          ),
        },
        {
          kind: "p",
          text: l(
            "**一次能生成多条轨道的动画吗？** 可以，勾几条就生成几条，输出文件名里的轨道段会写成 `tracks_2_3` 这样。整条轨都是泛音声部时，额外勾上它的 `🎵 泛音`。",
            "**Can I animate several tracks at once?** Yes — tick as many as you need and the track part of the output names becomes something like `tracks_2_3`. If a whole track is a harmonic part, tick its `🎵 泛音` / Harmonic as well.",
          ),
        },
        {
          kind: "p",
          text: l(
            "**失败想找人看怎么办？** 在控制台点 `复制全部`，把整段日志发出来，注明你选的演奏者和轨道号。如果怀疑是算出来的手型有问题，再去 `Poses 查看` 用 `📋 复制结果` 把那几条姿态记录补上——比截图有用得多。",
            "**Something failed and I need help.** Press `复制全部` / Copy All on the console and send the whole log, noting your performer and track numbers. If you suspect the computed hand shapes, add the matching pose records from `📋 复制结果` / Copy Result in `Poses 查看` / Poses Viewer — far more useful than a screenshot.",
          ),
        },
      ],
    },
  ],
};
